import classNames from 'classnames';
import _ from 'lodash';
import React, { Suspense, useEffect, useLayoutEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AppBanner from '../../banners/AppBanner';
import ModalContainer from '../../components/ModalContainer';
import ToastContainer from '../../components/ToastContainer';
import OnboardingContainer from '../../onboarding/OnboardingContainer';
import UIActions from '../../ui/UIActions';
import PageMeta from '../../utils/PageMeta';
import Sizes from '../../utils/responsive/Sizes';
import isSize from '../../utils/responsive/isSize';
import { IsPublicViewProvider } from '../../utils/useIsPublicView';
import { useUserUiTheme } from '../../utils/useUiTheme';
import useAppSelector, { useAppDispatch } from '../redux/useAppSelector';
import RightPanelContainer from './RightPanelContainer';

export interface PageLayoutElements {
  sidebar?: JSX.Element;
  topbar?: JSX.Element;
  helpMenu?: JSX.Element;
}

interface PageLayoutProps {
  elements: PageLayoutElements;
  routeName: string;
}

const PageLayout = ({
  elements: { sidebar, topbar, helpMenu },
  routeName,
}: PageLayoutProps) => {
  const theme = useUserUiTheme();

  useLayoutEffect(() => {
    if (theme) {
      document.body.setAttribute('data-theme', theme.toLowerCase());
    }

    return () => {
      document.body.removeAttribute('data-theme');
    };
  }, [theme]);

  const location = useLocation();
  const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);
  const dispatch = useAppDispatch();

  const isPublicView = location.pathname.startsWith('/public');

  useEffect(() => {
    if (sidebar && isSize(Sizes.SM)) {
      dispatch(UIActions.toggleSidebar(false));
    }
  }, [sidebar, location, dispatch]);

  const classes = classNames(
    'app-root',
    sidebar && isSidebarOpen ? 'sidebar' : 'no-sidebar',
  );

  return (
    <IsPublicViewProvider publicView={isPublicView}>
      <div id={`${routeName}-view`} className={classes}>
        <PageMeta title={`${_.startCase(routeName)} - Slab`} />
        <div className="pace" />
        <AppBanner />
        <ModalContainer />
        <ToastContainer />
        {sidebar}
        <main id="main">
          {topbar}
          <div id="inner">
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
            <RightPanelContainer />
            {helpMenu}
          </div>
        </main>
        <OnboardingContainer />
      </div>
    </IsPublicViewProvider>
  );
};

export default PageLayout;
